import { useMemo } from 'react';
import {
    Wallet,
    Star,
    TrendingUp,
    IndianRupee,
    Users,
    BarChart3,
    FileText,
    MapPin,
} from 'lucide-react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

import KPICard from '../components/common/KPICard';
import ChartCard from '../components/common/ChartCard';
import DataTable from '../components/common/DataTable';
import { useFilters } from '../context/FilterContext';
import { formatCurrency, formatNumber } from '../utils/formatters';
import styles from './OrderAnalytics.module.css';

// Group entries by date for trend charts
function buildDailyTrend(entries, label) {
    const dayMap = {};
    entries.forEach(e => {
        const d = e.date || 'Unknown';
        if (!dayMap[d]) dayMap[d] = { date: d, [label]: 0, count: 0 };
        dayMap[d][label] += e.amount;
        dayMap[d].count += 1;
    });
    return Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date));
}

// Group by city
function buildCityBreakdown(entries, valueKey, countKey) {
    const cityMap = {};
    entries.forEach(e => {
        const c = e.cityName || 'Unknown';
        if (!cityMap[c]) cityMap[c] = { name: c, [valueKey]: 0, [countKey]: 0 };
        cityMap[c][valueKey] += e.amount;
        cityMap[c][countKey] += 1;
    });
    return Object.values(cityMap).sort((a, b) => b[valueKey] - a[valueKey]);
}

// ──────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────
function WalletSubscriptions() {
    const { serviceOrders, appliedFilters, isLoadingOrders } = useFilters();

    // Apply date filter to service orders
    const filtered = useMemo(() => {
        if (!serviceOrders || serviceOrders.length === 0) return [];
        const start = appliedFilters.dateRange?.startDate
            ? appliedFilters.dateRange.startDate.toISOString().split('T')[0] : null;
        const end = appliedFilters.dateRange?.endDate
            ? appliedFilters.dateRange.endDate.toISOString().split('T')[0] : null;
        return serviceOrders.filter(o => {
            if (!o.date) return true;
            if (start && o.date < start) return false;
            if (end && o.date > end) return false;
            return true;
        });
    }, [serviceOrders, appliedFilters.dateRange]);

    // Split
    const walletOrders = useMemo(() => filtered.filter(o => o.tranSubTypeId === 'SBSCR'), [filtered]);
    const subsOrders = useMemo(() => filtered.filter(o => o.tranSubTypeId === 'PREPD'), [filtered]);

    // Wallet metrics
    const walletMetrics = useMemo(() => ({
        count: walletOrders.length,
        totalAmount: walletOrders.reduce((s, o) => s + o.amount, 0),
        avgAmount: walletOrders.length > 0 ? walletOrders.reduce((s, o) => s + o.amount, 0) / walletOrders.length : 0,
        uniqueCustomers: new Set(walletOrders.map(o => o.customerId)).size,
    }), [walletOrders]);

    const subsMetrics = useMemo(() => ({
        count: subsOrders.length,
        totalAmount: subsOrders.reduce((s, o) => s + o.amount, 0),
        avgAmount: subsOrders.length > 0 ? subsOrders.reduce((s, o) => s + o.amount, 0) / subsOrders.length : 0,
        uniqueCustomers: new Set(subsOrders.map(o => o.customerId)).size,
    }), [subsOrders]);

    // Trend data
    const walletTrend = useMemo(() => buildDailyTrend(walletOrders, 'Amount'), [walletOrders]);
    const subsTrend = useMemo(() => buildDailyTrend(subsOrders, 'Amount'), [subsOrders]);

    // City breakdowns
    const walletByCity = useMemo(() => buildCityBreakdown(walletOrders, 'Amount', 'Count'), [walletOrders]);
    const subsByCity = useMemo(() => buildCityBreakdown(subsOrders, 'Amount', 'Count'), [subsOrders]);

    // Combined city chart (side by side)
    const cityCompare = useMemo(() => {
        const cities = new Set([
            ...walletByCity.map(c => c.name),
            ...subsByCity.map(c => c.name),
        ]);
        return [...cities].map(city => ({
            name: city.length > 14 ? city.slice(0, 14) + '…' : city,
            'Wallet': walletByCity.find(c => c.name === city)?.Amount || 0,
            'Subscriptions': subsByCity.find(c => c.name === city)?.Amount || 0,
        })).sort((a, b) => (b['Wallet'] + b['Subscriptions']) - (a['Wallet'] + a['Subscriptions']));
    }, [walletByCity, subsByCity]);

    // Combined table columns
    const tableColumns = [
        { key: 'saleOrderId', label: 'Order ID' },
        { key: 'date', label: 'Date' },
        {
            key: 'type', label: 'Type',
            render: v => (
                <span style={{
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: v === 'Wallet Recharge' ? 'rgba(74,144,226,0.15)' : 'rgba(80,200,120,0.15)',
                    color: v === 'Wallet Recharge' ? '#4A90E2' : '#10B981',
                }}>
                    {v}
                </span>
            )
        },
        { key: 'amount', label: 'Amount', render: v => formatCurrency(v) },
        {
            key: 'orderStatus', label: 'Status',
            render: v => {
                const cfg = {
                    'Delivered':        { bg: 'rgba(16,185,129,0.15)',  color: '#10B981' },
                    'Pending':          { bg: 'rgba(245,158,11,0.15)',  color: '#F59E0B' },
                    'Cancelled':        { bg: 'rgba(239,68,68,0.15)',   color: '#EF4444' },
                    'Out for Delivery': { bg: 'rgba(99,102,241,0.15)',  color: '#6366F1' },
                }[v] || { bg: 'rgba(107,114,128,0.15)', color: '#6B7280' };
                return (
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: cfg.bg, color: cfg.color }}>
                        {v}
                    </span>
                );
            }
        },
        { key: 'customerName', label: 'Customer' },
        { key: 'customerId', label: 'Customer ID' },
        { key: 'cityName', label: 'City' },
        { key: 'storeName', label: 'Store' },
        { key: 'billNo', label: 'Bill No', render: v => v || '-' },
    ];

    const tooltipStyle = { background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 };

    if (isLoadingOrders) {
        return (
            <div className={styles.page}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, flexDirection: 'column', gap: 12 }}>
                    <div style={{ width: 36, height: 36, border: '3px solid var(--color-border)', borderTopColor: 'var(--color-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>Loading wallet & subscription data…</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1 className={styles.title}>Wallet &amp; Subscriptions</h1>
                <p className={styles.subtitle}>Wallet recharges (SBSCR) and prepaid subscriptions (PREPD) tracked separately from retail orders</p>
            </header>

            {/* ═══════════════════════════════
                SUBSCRIPTION SECTION
            ═══════════════════════════════ */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <Star size={20} className={styles.sectionIcon} style={{ color: '#10B981' }} />
                    Subscriptions (Prepaid)
                </h2>

                <div className={styles.kpiGrid4}>
                    <KPICard title="Total Subscriptions" value={formatNumber(subsMetrics.count)} icon={Star} color="success" />
                    <KPICard title="Total Revenue" value={formatCurrency(subsMetrics.totalAmount, { compact: true })} icon={IndianRupee} color="warning" />
                    <KPICard title="Avg Subscription" value={formatCurrency(subsMetrics.avgAmount)} icon={TrendingUp} color="info" />
                    <KPICard title="Unique Subscribers" value={formatNumber(subsMetrics.uniqueCustomers)} icon={Users} color="secondary" />
                </div>

                <div className={styles.chartsGrid}>
                    <ChartCard title="Daily Subscription Revenue" icon={TrendingUp} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={subsTrend}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v?.slice(5)} />
                                <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                                <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                                <Line type="monotone" dataKey="Amount" stroke="#10B981" strokeWidth={2} dot={false} name="Subscription Amount" />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Subscriptions by City" icon={MapPin} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={subsByCity.slice(0, 10)} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={90} tickFormatter={v => v.length > 12 ? v.slice(0, 12) + '…' : v} />
                                <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                                <Bar dataKey="Amount" fill="#10B981" radius={[0, 4, 4, 0]} name="Subscription Amount" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </section>

            {/* ═══════════════════════════════
                WALLET RECHARGE SECTION
            ═══════════════════════════════ */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <Wallet size={20} className={styles.sectionIcon} style={{ color: '#4A90E2' }} />
                    Wallet Recharges
                </h2>

                <div className={styles.kpiGrid4}>
                    <KPICard title="Total Recharges" value={formatNumber(walletMetrics.count)} icon={Wallet} color="primary" />
                    <KPICard title="Total Recharged" value={formatCurrency(walletMetrics.totalAmount, { compact: true })} icon={IndianRupee} color="success" />
                    <KPICard title="Avg Recharge" value={formatCurrency(walletMetrics.avgAmount)} icon={TrendingUp} color="info" />
                    <KPICard title="Unique Customers" value={formatNumber(walletMetrics.uniqueCustomers)} icon={Users} color="secondary" />
                </div>

                <div className={styles.chartsGrid}>
                    <ChartCard title="Daily Recharge Amount" icon={TrendingUp} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={walletTrend}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v?.slice(5)} />
                                <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                                <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                                <Line type="monotone" dataKey="Amount" stroke="#4A90E2" strokeWidth={2} dot={false} name="Amount Recharged" />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Recharges by City" icon={MapPin} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={walletByCity.slice(0, 10)} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={90} tickFormatter={v => v.length > 12 ? v.slice(0, 12) + '…' : v} />
                                <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                                <Bar dataKey="Amount" fill="#4A90E2" radius={[0, 4, 4, 0]} name="Recharge Amount" />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </section>

            {/* ═══════════════════════════════
                COMBINED CITY COMPARISON
            ═══════════════════════════════ */}
            {cityCompare.length > 0 && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>
                        <BarChart3 size={20} className={styles.sectionIcon} />
                        Wallet vs Subscriptions by City
                    </h2>
                    <ChartCard title="Wallet Recharge vs Subscription Revenue — by City" icon={BarChart3} height={300}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={cityCompare.slice(0, 12)}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                                <Tooltip contentStyle={tooltipStyle} formatter={v => formatCurrency(v)} />
                                <Legend />
                                <Bar dataKey="Wallet" fill="#4A90E2" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Subscriptions" fill="#10B981" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </section>
            )}

            {/* ═══════════════════════════════
                COMBINED RAW TABLE
            ═══════════════════════════════ */}
            <section className={styles.section}>
                <DataTable
                    data={[...filtered].sort((a, b) => (b.date || '').localeCompare(a.date || ''))}
                    columns={tableColumns}
                    title="All Wallet & Subscription Transactions"
                    icon={FileText}
                    exportFilename="wallet_subscriptions"
                    defaultPageSize={15}
                />
            </section>
        </div>
    );
}

export default WalletSubscriptions;
